import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import {
  ClientType,
  validateClientIdentifierInput,
} from "../utils/client-identifier";

/**
 * Permissive phone format. Real users routinely paste:
 *   "+94 77 123 4567"
 *   "0771234567 / 0117654321"
 *   "077-123-4567"
 *   "(011) 234 5678"
 * Any of those would be rejected by a digits-only regex.
 *
 * Rules:
 * - Blank is valid (use Validators.required separately when needed).
 * - Allowed chars: digits, +, -, (, ), space, /
 * - At least 7 digits total (covers Sri Lankan & international formats).
 */
export function phoneValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const v = control.value;
  if (v === null || v === undefined || v === "") return null;
  const s = String(v).trim();
  if (s === "") return null;
  const allowed = /^[\d+\-()/\s]+$/.test(s);
  if (!allowed) return { phone: true };
  const digitCount = (s.match(/\d/g) || []).length;
  if (digitCount < 7) return { phone: true };
  return null;
}

/**
 * Permissive name validator. Accepts Unicode letters, spaces, dots,
 * hyphens, and apostrophes. Examples that previously failed:
 *   "O'Neil"
 *   "A.M. Perera"
 *   Sinhala / Tamil names with non-Latin glyphs.
 *
 * Blank is treated as valid; pair with Validators.required when needed.
 */
export function nameValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const v = control.value;
  if (v === null || v === undefined || v === "") return null;
  const s = String(v);
  // \p{L}: any Unicode letter, \p{M}: combining marks (for Sinhala/Tamil)
  const valid = /^[\p{L}\p{M}\s.\-']+$/u.test(s);
  return valid ? null : { name: true };
}

/**
 * Numeric-only validator that treats blank as valid. Suitable for
 * truly-numeric fields like age. Phone numbers should use phoneValidator
 * instead because users format them with spaces / +.
 */
export function numericValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const v = control.value;
  if (v === null || v === undefined || v === "") return null;
  const valid = /^\d+$/.test(String(v));
  return valid ? null : { numeric: true };
}

/** Validates REG-/NREG-/BHT- client numbers (digits-only or full canonical form). */
export function clientIdentifierValidator(
  clientType: ClientType | (() => ClientType),
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = control.value;
    if (v === null || v === undefined || v === "") return null;
    const type = typeof clientType === "function" ? clientType() : clientType;
    const result = validateClientIdentifierInput(type, String(v));
    return result.valid ? null : { invalidIdentifier: true };
  };
}
